import React, { useEffect, useState } from 'react';
import { getDepartments, updateDepartmentPermissions } from '../services/employeeService';
import { DAILY_ENTRY_FIELDS, PUNCH_POINT_FIELDS } from '../constants/fieldPermissions';
import useUIStore from '../store/uiStore';
import DepartmentHeadsCard from '../components/DepartmentHeadsCard';
import ReportRecipientsCard from '../components/ReportRecipientsCard';

const PermissionsPage = () => {
  const [departments, setDepartments] = useState([]);
  const [departmentId, setDepartmentId] = useState('');
  const [permissions, setPermissions] = useState({ daily_entry: {}, punch_point: {} });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const showToast = useUIStore((s) => s.showToast);

  const load = () => {
    setLoading(true);
    getDepartments()
      .then((res) => setDepartments(res.data.departments || []))
      .catch(() => showToast('Failed to load departments', 'error'))
      .finally(() => setLoading(false));
  };

  useEffect(load, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const dept = departments.find((d) => String(d.id) === String(departmentId));
    setPermissions({
      daily_entry: { ...(dept?.field_permissions?.daily_entry || {}) },
      punch_point: { ...(dept?.field_permissions?.punch_point || {}) },
    });
  }, [departmentId, departments]);

  const toggle = (form, key) => {
    setPermissions((prev) => ({
      ...prev,
      [form]: { ...prev[form], [key]: prev[form][key] === false ? true : false },
    }));
  };

  const isChecked = (form, key) => permissions[form]?.[key] !== false;

  const handleSave = async () => {
    if (!departmentId) return;
    setSaving(true);
    try {
      await updateDepartmentPermissions(departmentId, permissions);
      showToast('Permissions saved', 'success');
      load();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to save permissions', 'error');
    }
    setSaving(false);
  };

  return (
    <div className="min-h-screen p-6">
      <div className="max-w-2xl mx-auto">
        <h1 className="text-xl font-bold text-white mb-4">Permissions</h1>

        <DepartmentHeadsCard />

        <ReportRecipientsCard />

        <p className="text-sm text-gray-500 mb-6">
          Choose which optional fields a department's users see on Daily Entry and Punch Points.
          This only hides fields in the app for convenience — it is not a security restriction.
        </p>

        <div className="glass-card p-6">
          <label className="block text-sm font-medium text-gray-700 mb-1">Department</label>
          <select
            value={departmentId}
            onChange={(e) => setDepartmentId(e.target.value)}
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm mb-6"
            disabled={loading}
          >
            <option value="">Select a department</option>
            {departments.map((d) => (
              <option key={d.id} value={d.id}>{d.name}</option>
            ))}
          </select>

          {departmentId && (
            <>
              <div className="mb-6">
                <h2 className="font-semibold text-gray-900 mb-2">Daily Entry</h2>
                <div className="border border-gray-200 rounded divide-y divide-gray-100">
                  {DAILY_ENTRY_FIELDS.map((f) => (
                    <label key={f.key} className="flex items-center justify-between px-3 py-2 text-sm cursor-pointer">
                      <span>{f.label}</span>
                      <input
                        type="checkbox"
                        checked={isChecked('daily_entry', f.key)}
                        onChange={() => toggle('daily_entry', f.key)}
                      />
                    </label>
                  ))}
                </div>
              </div>

              <div className="mb-6">
                <h2 className="font-semibold text-gray-900 mb-2">Punch Points</h2>
                <div className="border border-gray-200 rounded divide-y divide-gray-100">
                  {PUNCH_POINT_FIELDS.map((f) => (
                    <label key={f.key} className="flex items-center justify-between px-3 py-2 text-sm cursor-pointer">
                      <span>{f.label}</span>
                      <input
                        type="checkbox"
                        checked={isChecked('punch_point', f.key)}
                        onChange={() => toggle('punch_point', f.key)}
                      />
                    </label>
                  ))}
                </div>
              </div>

              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 text-sm rounded bg-komal-blue hover:bg-komal-blue-dark text-white font-semibold disabled:opacity-60"
              >
                {saving ? 'Saving...' : 'Save Permissions'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PermissionsPage;
